import type { ApiCourseBatch } from '@/types/course'

// วันที่ของรุ่นอบรมจาก API เป็นเวลาไทยแบบไม่มี timezone (เช่น "2026-10-05T23:59:00") แต่ new Date(string) ตีความเป็นเวลาเครื่องผู้ใช้
// จึงต้องเทียบกับ "เวลาไทยตอนนี้" ในรูปแบบเดียวกัน (ไม่ใช่ Date.now() ตรงๆ) — ไม่งั้นผู้ใช้ที่เครื่องอยู่ timezone อื่นจะเห็นผิดช่วงหลายชั่วโมง
export const bangkokNowMs = (): number => Date.now() + (7 * 60 + new Date().getTimezoneOffset()) * 60000

/** วันปิดรับสมัครของรุ่นผ่านไปแล้วหรือยัง — ตรงกับเงื่อนไขใน ensure_batch_bookable ฝั่ง backend (ผู้ตัดสินจริงตอนชำระเงิน) */
export const registrationDeadlinePassed = (batch: ApiCourseBatch, now: number = bangkokNowMs()): boolean =>
  !!batch.registration_deadline && new Date(batch.registration_deadline).getTime() < now

const DAY_MS = 24 * 60 * 60 * 1000

export const batchEndMs = (b: ApiCourseBatch): number => new Date(b.end_date || b.start_date).getTime()

/** รุ่นนี้เลขที่เท่าไร — batch_code รูปแบบ "<รหัสคอร์ส>-B<เลขรุ่น>" */
export const batchNumber = (b: ApiCourseBatch): string => b.batch_code.split('-B').pop() || b.batch_code

export type BatchTone = 'open' | 'almost' | 'full' | 'closed' | 'past'

export interface BatchState {
  isPast: boolean
  isFull: boolean
  closed: boolean            // กดเลือกสมัครไม่ได้ (ผ่านไปแล้ว / ปิดรับ / เต็ม)
  limited: boolean
  booked: number
  seatsLeft: number | null
  percentFilled: number
  almostFull: boolean
  deadlinePassed: boolean
  daysToDeadline: number | null
  tone: BatchTone
  label: string              // ข้อความป้ายสถานะ
}

/** สถานะของรุ่น — ใช้ร่วมกันทั้งตารางรุ่นอบรมและการ์ดข้างจอ ให้ตัดสินตรงกันเสมอ */
export function getBatchState(batch: ApiCourseBatch, now: number): BatchState {
  const limited = batch.max_seats !== -1
  const booked = batch.registered_count
  const seatsLeft = limited ? Math.max(batch.max_seats - booked, 0) : null
  const isFull = seatsLeft === 0
  const isPast = batchEndMs(batch) < now
  const deadlinePassed = registrationDeadlinePassed(batch, now)
  const closed = isPast || deadlinePassed || batch.status !== 'OPEN' || isFull
  const almostFull = limited && !closed && seatsLeft! <= Math.max(Math.ceil(batch.max_seats * 0.2), 3)
  const percentFilled = limited ? Math.min(Math.round((booked / batch.max_seats) * 100), 100) : 0
  const deadline = batch.registration_deadline ? new Date(batch.registration_deadline).getTime() : null
  const daysToDeadline = deadline ? Math.ceil((deadline - now) / DAY_MS) : null

  let tone: BatchTone
  let label: string
  if (isPast) { tone = 'past'; label = 'ผ่านไปแล้ว' }
  else if (isFull) { tone = 'full'; label = 'เต็มแล้ว' }
  else if (deadlinePassed || batch.status !== 'OPEN') { tone = 'closed'; label = 'ปิดรับสมัคร' }
  else if (almostFull) { tone = 'almost'; label = 'ที่นั่งใกล้เต็ม' }
  else { tone = 'open'; label = 'เปิดรับสมัคร' }

  return { isPast, isFull, closed, limited, booked, seatsLeft, percentFilled, almostFull, deadlinePassed, daysToDeadline, tone, label }
}

/** "รุ่นปัจจุบันที่เปิด" = รุ่นที่ยังสมัครได้ซึ่งเริ่มเร็วที่สุด — ใช้ร่วมกันทั้งการ์ดเลือกรุ่นข้างจอและสถานที่อบรมเริ่มต้น */
export function currentOpenBatch(batches: ApiCourseBatch[], now: number = bangkokNowMs()): ApiCourseBatch | null {
  const sorted = [...batches].sort((a, b) => a.start_date.localeCompare(b.start_date))
  return sorted.find((b) => !getBatchState(b, now).closed) || null
}
